/**
 * Report comments and plugins.
 *  - A comment thread belongs to one user share of a report: the recipient (usually View Only)
 *    and the member who shared it talk privately; nobody else sees the thread.
 *  - Plugins are connected by Project Admins for the whole organization or one project; a project
 *    setting overrides the organization's for that project.
 */
import { z } from 'zod';

export const COMMENT_MAX_LENGTH = 4000;
export const commentBodySchema = z.object({ body: z.string().trim().min(1).max(COMMENT_MAX_LENGTH) }).strict();

export interface ReportCommentView {
  id: string;
  body: string;
  createdAt: string;
  author: { id: string; name: string };
  /** When the other person in the thread first saw it (null = unread by them). */
  readAt: string | null;
}

/** One private conversation about a report between its recipient and the member who shared it. */
export interface CommentThreadView {
  shareId: string;
  viewer: { id: string; name: string; email: string };
  reporter: { id: string; name: string; email: string };
  comments: ReportCommentView[];
  /** Comments from the other person the caller has not seen yet (before this request). */
  unread: number;
}

export const PLUGIN_KEY_RE = /^[a-z0-9][a-z0-9-]{0,48}$/;

export const pluginUpsertSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    enabled: z.boolean(),
    config: z.record(z.unknown()).default({}),
    // Name of an env var / vault key that holds the secret - never the secret itself.
    secretRef: z
      .string()
      .regex(/^[A-Z][A-Z0-9_]{0,100}$/, 'secretRef must be an env var name like SLACK_WEBHOOK_URL')
      .nullish(),
    /** Set to configure the plugin for one project instead of the whole organization. */
    projectId: z.string().min(1).nullish(),
    /**
     * Notification plugins: the webhook URL or SMTP password, stored encrypted and never returned.
     * Omit to keep the stored one, null to remove it.
     */
    secret: z.string().max(2000).nullish(),
  })
  .strict();
export type PluginUpsert = z.infer<typeof pluginUpsertSchema>;
